import { useState } from "react";

import { useDeleteVectorStoreFile } from "@/queries/vector-store";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseFileManagementProps {
  selectedVectorStoreId: string | null;
}

export const useFileManagement = ({ selectedVectorStoreId }: UseFileManagementProps) => {
  const { mutate: deleteFile, isLoading: isDeletingFile } = useDeleteVectorStoreFile();
  const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(new Set());

  const handleDeleteFile = (fileId: string) => {
    if (!selectedVectorStoreId) {
      return;
    }

    setDeletingFileIds(prev => new Set(prev).add(fileId));

    deleteFile(
      {
        vector_store_id: selectedVectorStoreId,
        file_id: fileId,
        onConfirmed: () => {
          // Show toast only after file deletion is confirmed
          displayToast("File deleted successfully", ToastType.success);
          setDeletingFileIds(prev => {
            const next = new Set(prev);
            next.delete(fileId);
            return next;
          });
        },
      },
      {
        onError: (error: unknown) => {
          const getErrorMessage = (err: unknown, defaultMessage: string): string => {
            if (typeof err === "string") {
              return err;
            }

            if (err instanceof Error) {
              return err.message;
            }
            return defaultMessage;
          };

          const errorMessage = getErrorMessage(error, "Failed to delete file");
          displayErrorToast(errorMessage);
          setDeletingFileIds(prev => {
            const next = new Set(prev);
            next.delete(fileId);
            return next;
          });
        },
      },
    );
  };

  const isDeletingFileById = (fileId: string) => deletingFileIds.has(fileId);

  return {
    handleDeleteFile,
    isDeletingFile: isDeletingFile || deletingFileIds.size > 0,
    isDeletingFileById,
  };
};
